import {
  ApiResponseError,
  createApiClient,
  createLubeHubApi,
  type LubeHubApi,
  type VehicleListing,
} from '@lubehub/api';
import { Badge, Button, EmptyState, entrance, ErrorState, Icon, Skeleton } from '@lubehub/ui-web';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { apiBaseUrl } from '../config';
import { t } from '../copy';
import { formatAmount, formatCount } from '../format';
import { PRODUCT_AR } from '../legal/identity';
import { usePublicLocale } from '../locale';

/**
 * The deep link the mobile application answers to.
 *
 * The page itself is the HTTPS link. This button is the custom-scheme
 * fallback for a browser that stayed on the page: it opens the same listing,
 * not the home screen. A phone without the app ignores the scheme and stays
 * here.
 */
const APP_SCHEME = 'lubehub://explore/cars/';

/**
 * A car for sale, for somebody who does not have the application.
 *
 * **The whole point is that the recipient of a WhatsApp link can see the car
 * without installing anything.** A share that landed on "download the app" and
 * nothing else would not be a share; it would be an advertisement for us at the
 * expense of the person trying to sell their car.
 *
 * It renders the **public** resource and nothing else: the API has no endpoint
 * that would give this page the VIN, the plate, the owner's identity, the car's
 * private notes or any invoice, so the boundary is not this page's restraint —
 * it is the contract.
 *
 * **A known limitation, recorded rather than papered over.** `apps/web` is a
 * static Vite bundle on Plesk with no server-side rendering, so the title and
 * description below are set from JavaScript. Browsers and search engines that
 * execute JavaScript will see them; **WhatsApp and Facebook will not** — they
 * read the HTML they are served, and a static host serves the same
 * `index.html` for every listing. Per-listing Open Graph cards need a prerender
 * step or an SSR endpoint, which is a deployment change. The link works and the
 * page is correct; the rich preview card is the part that is missing. See
 * `docs/plans/vehicle-marketplace.md`.
 */
export interface CarListingPageProps {
  /** Tests inject a stub so no network call is made. */
  apiFactory?: () => LubeHubApi;
}

export function CarListingPage({ apiFactory }: CarListingPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const { locale } = usePublicLocale();

  const [listing, setListing] = useState<VehicleListing | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  // The listing and status belong to the id they were loaded for: a new id
  // starts from "loading" instead of showing the previous car.
  const [loadedFor, setLoadedFor] = useState(id);

  if (loadedFor !== id) {
    setLoadedFor(id);
    setListing(null);
    setStatus('loading');
  }

  const api = useMemo(
    () => apiFactory?.() ?? createLubeHubApi(createApiClient({ baseUrl: apiBaseUrl() })),
    [apiFactory],
  );

  useEffect(() => {
    const controller = new AbortController();
    const listingId = Number(id);

    if (!Number.isFinite(listingId)) {
      queueMicrotask(() => {
        setStatus('missing');
      });

      return () => {
        controller.abort();
      };
    }

    api.marketplace
      .get(listingId, { signal: controller.signal })
      .then((found: VehicleListing) => {
        setListing(found);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        // A request this page cancelled (StrictMode, or a new id) is not a
        // missing listing; it used to flash "no longer available".
        if (controller.signal.aborted) {
          return;
        }

        // Withdrawn and never-existed are a 404; anything else is a failure
        // to reach the server, and says so with a way to try again.
        setStatus(error instanceof ApiResponseError && error.isNotFound ? 'missing' : 'error');
      });

    return () => {
      controller.abort();
    };
  }, [api, id, attempt]);

  useEffect(() => {
    if (listing === null) {
      return;
    }

    const name = `${listing.make} ${listing.model} ${String(listing.year)}`;
    const price = formatAmount(listing.asking_price, listing.currency);
    const description = document.querySelector('meta[name="description"]');
    const previousTitle = document.title;
    const previousDescription = description?.getAttribute('content') ?? null;

    document.title = `${name} — ${price} · ${PRODUCT_AR}`;
    description?.setAttribute('content', `${name} · ${price}`);

    // Restored on the way out, or the car's title stuck to the next page.
    return () => {
      document.title = previousTitle;

      if (previousDescription !== null) {
        description?.setAttribute('content', previousDescription);
      }
    };
  }, [listing]);

  if (status === 'loading') {
    return (
      <div className="public-car" data-testid="car-loading" aria-busy="true">
        <span className="lh-visually-hidden" role="status">
          {t(locale, 'car.loading')}
        </span>
        <Skeleton height={320} radius="md" className="public-car__media" />
        <div className="public-car__panel">
          <Skeleton height={36} width="60%" />
          <Skeleton height={20} width="40%" />
          <Skeleton height={120} radius="md" />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="public-state" data-testid="car-error">
        <ErrorState
          headingLevel="h1"
          title={t(locale, 'car.error.title')}
          description={t(locale, 'car.error.body')}
          icon={<Icon name="alert" size={24} />}
          action={
            <Button
              onClick={() => {
                setStatus('loading');
                setAttempt((value) => value + 1);
              }}
            >
              {t(locale, 'car.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  if (status === 'missing' || listing === null) {
    return (
      <div className="public-state" data-testid="car-missing">
        <EmptyState
          headingLevel="h1"
          title={t(locale, 'car.missing.title')}
          description={t(locale, 'car.missing.body')}
          icon={<Icon name="car" size={24} />}
          action={
            <Link to="/" className="lh-button lh-button--secondary">
              <span className="lh-button__label">{t(locale, 'notFound.home')}</span>
            </Link>
          }
        />
      </div>
    );
  }

  const arrival = entrance();
  const name = `${listing.make} ${listing.model} ${String(listing.year)}`;
  const price = formatAmount(listing.asking_price, listing.currency);
  const cover = listing.photos.find((photo) => photo.is_cover) ?? listing.photos[0];

  return (
    <article
      className={['public-car', arrival.className].filter(Boolean).join(' ')}
      style={arrival.style}
      data-testid="car-page"
    >
      <figure className="public-car__media">
        {listing.cover_url === null ? (
          <span className="public-car__placeholder" aria-hidden="true">
            <Icon name="car" size={64} />
          </span>
        ) : (
          <img
            src={listing.cover_url}
            alt={name}
            className="public-car__image"
            data-testid="car-image"
          />
        )}

        {/*
          The photograph's licence, under the photograph. A CC-BY image is
          licensed on the condition that its author and licence are named
          wherever it appears; a seller's own photograph carries no credit.
        */}
        {cover?.credit == null ? null : (
          <figcaption className="public-car__credit" data-testid="car-photo-credit">
            {t(locale, 'car.photoCredit')} {cover.credit.author} —{' '}
            {cover.credit.license_url === null ? (
              cover.credit.license
            ) : (
              <a href={cover.credit.license_url} rel="noreferrer nofollow" target="_blank">
                {cover.credit.license}
              </a>
            )}
          </figcaption>
        )}
      </figure>

      <div className="public-car__panel">
        <div className="public-car__heading">
          <p className="public-car__name" data-testid="car-name">
            {name}
          </p>
          <h1 className="public-car__price lh-numeric" data-testid="car-price">
            {price}
          </h1>
          {listing.status === 'sold' ? (
            <p className="public-car__sold" data-testid="car-sold">
              <Badge tone="neutral">{t(locale, 'car.sold')}</Badge>
            </p>
          ) : null}
        </div>

        <dl className="public-car__specs" data-testid="car-specs">
          {listing.odometer === null ? null : (
            <div>
              <dt>
                <Icon name="gauge" size={16} /> {t(locale, 'car.odometer')}
              </dt>
              <dd className="lh-numeric">{`${formatCount(listing.odometer)} ${t(locale, 'car.km')}`}</dd>
            </div>
          )}
          <div>
            <dt>
              <Icon name="location" size={16} /> {t(locale, 'car.place')}
            </dt>
            <dd>{listing.city ?? listing.governorate}</dd>
          </div>
        </dl>

        {listing.has_service_history ? (
          <p className="public-car__history" data-testid="car-history">
            <Icon name="history" size={18} />
            {t(locale, 'car.hasHistory')}
          </p>
        ) : null}

        {listing.description === null ? null : (
          <p className="public-car__description" data-testid="car-description">
            {listing.description}
          </p>
        )}

        <a
          className="lh-button lh-button--primary lh-button--large lh-button--full"
          href={`${APP_SCHEME}${String(listing.id)}`}
          data-testid="car-open-app"
        >
          <span className="lh-button__label">{t(locale, 'car.openApp')}</span>
        </a>
      </div>
    </article>
  );
}
